import { PortalDomainOrg, PortalOrder, PortalOrgUiConfigSection } from '../../types'
import { NotificationsInfo } from '../../utils'
import { SectionIcon } from './Section'
import Icon from '@mui/material/Icon'

export type SectionConfig = {
  type: SectionName;
  icon: JSX.Element;
  name: string;
  isStandaloneSection: boolean
  secondaryText: string;
  badge?: number;
  routeTo: null | string,
  searchParams: any;
  hideInClosingPage?: boolean;
  sortOrder?: number;
  contentToken?: string;
  contentTokenValue?: string;
  displayType?: 'individualPage' | 'closingPage';
}

// Wonder why this isn't working:
// export type SectionName = keyof typeof sectionMap
export type SectionName = string;

export const sectionMap: {[key: string]: SectionConfig} = {
  'notifications': {
    type: 'notifications',
    icon: <SectionIcon icon={<Icon className="material-icons-outlined">mail</Icon>} />,
    name: 'Messages',
    isStandaloneSection: true,
    secondaryText: '',
    routeTo: null,
    searchParams: { component: 'messages' },
  },
  'closing-documents': {
    type: 'closing-documents',
    icon: <SectionIcon icon={<Icon className="material-icons-outlined">folder</Icon>} />,
    name: 'Documents',
    isStandaloneSection: false,
    secondaryText: '',
    routeTo: null,
    searchParams: {},
  },
  'wiring-instructions': {
    type: 'wiring-instructions',
    icon: <SectionIcon icon={<Icon className="material-icons-outlined">bolt</Icon>} />,
    name: 'Wire Transfer Instructions',
    isStandaloneSection: false,
    secondaryText: '',
    routeTo: null,
    searchParams: {},
  },
  'closing-contacts': {
    type: 'closing-contacts',
    icon: <SectionIcon icon={<Icon className="material-icons-outlined">group</Icon>} />,
    name: 'Contacts',
    isStandaloneSection: false,
    secondaryText: '',
    routeTo: null,
    searchParams: {},
  },
  'tasks': {
    type: 'tasks',
    icon: <SectionIcon icon={<Icon className="material-icons-outlined">check</Icon>} />,
    name: 'Tasks',
    isStandaloneSection: false,
    secondaryText: '',
    routeTo: 'notifications',
    searchParams: {},
  },
}

/**
 * Returns an array of sections to render, based on the presence of data like
 * notifications and UiConfig.  Note the tracker and closing details are
 * currently handled special by the ui so are returned separately.
 */
export function getSectionsConfig({ notificationsInfo, order, org }: {
  notificationsInfo: NotificationsInfo;
  order: PortalOrder;
  org: PortalDomainOrg;
}) {
  let section: any
  let sectionsConfig: SectionConfig[] = []

  if (notificationsInfo.hasTasks) {
    section = org.UiConfig?.Sections?.Tasks

    if (section?.Enabled !== false) {
      sectionsConfig.push({
        ...sectionMap['tasks'],
        secondaryText: notificationsInfo.tasksCountText,
        badge: notificationsInfo.incompleteTasks.length,
        name: section?.Title || sectionMap['tasks'].name,
        sortOrder: section?.SortOrder,
        icon: section?.Icon ? <SectionIcon icon={<Icon className="material-icons-outlined">{section.Icon.value}</Icon>} /> : sectionMap['tasks'].icon,
      })
    }
  }

  if (notificationsInfo.hasMessages) {
    section = org.UiConfig?.Sections?.Messages

    if (section?.Enabled !== false) {
      sectionsConfig.push({
        ...sectionMap['notifications'],
        secondaryText: notificationsInfo.messagesCountText,
        badge: notificationsInfo.unreadMessages.length,
        name: section?.Title || sectionMap['notifications'].name,
        sortOrder: section?.SortOrder,
        icon: section?.Icon ? <SectionIcon icon={<Icon className="material-icons-outlined">{section.Icon.value}</Icon>} /> : sectionMap['notifications'].icon,
      })
    }
  }

  // HACK - if we have tasks but not messages, add in a notifications section
  // which renders the notifications page as a child.  We
  // mark it to hide its summary view.
  if (sectionsConfig.find(c => c.type === 'tasks') && !sectionsConfig.find(c => c.type === 'notifications')) {
    sectionsConfig.push({
      ...sectionMap['notifications'],
      hideInClosingPage: true,
      sortOrder: section?.SortOrder,
      icon: section?.Icon ? <SectionIcon icon={<Icon className="material-icons-outlined">{section.Icon.value}</Icon>} /> : sectionMap['notifications'].icon,
    })
  }

  section = org.UiConfig?.Sections?.ClosingDocuments
  if (section?.Enabled !== false) {
    sectionsConfig.push({
      ...sectionMap['closing-documents'],
      name: section?.Title || sectionMap['closing-documents'].name,
      sortOrder: section?.SortOrder,
      icon: section?.Icon ? <SectionIcon icon={<Icon className="material-icons-outlined">{section.Icon.value}</Icon>} /> : sectionMap['closing-documents'].icon,
    })
  }

  if (order.WiringInstructions) {
    section = org.UiConfig?.Sections?.WiringInstructions

    if (section?.Enabled !== false) {
      sectionsConfig.push({
        ...sectionMap["wiring-instructions"],
        secondaryText: notificationsInfo.wiringInstructionsTaskText,
        name: section?.Title || sectionMap["wiring-instructions"].name,
        sortOrder: section?.SortOrder,
        icon: section?.Icon ? <SectionIcon icon={<Icon className="material-icons-outlined">{section.Icon.value}</Icon>} /> : sectionMap['wiring-instructions'].icon,
      });
    }
  }

  if (order.ClosingContacts) {
    section = org.UiConfig?.Sections?.ClosingContacts

    if (section?.Enabled !== false) {
      sectionsConfig.push({
        ...sectionMap['closing-contacts'],
        name: section?.Title || sectionMap['closing-contacts'].name,
        sortOrder: section?.SortOrder,
        icon: section?.Icon ? <SectionIcon icon={<Icon className="material-icons-outlined">{section.Icon.value}</Icon>} /> : sectionMap['closing-contacts'].icon,
      })
    }
  }

  let closingTrackerSectionConfig = null
  section = org.UiConfig?.Sections?.Tracker
  if (section?.Enabled !== false) {
    closingTrackerSectionConfig = {
      name: section?.Title || 'Status Tracker'
    }
  }

  let closingDetailsSectionConfig = null
  section = org.UiConfig?.Sections?.ClosingDetails
  if (section?.Enabled !== false) {
    closingDetailsSectionConfig = {
      name: section?.Title || 'Signing Details',
    }
  }
  //Add custom sections
  Object.keys(org.UiConfig?.Sections || {}).forEach(key => {
    if (key.startsWith('CustomSection')) {
      const customSection: PortalOrgUiConfigSection | undefined = (org.UiConfig?.Sections ?? {})[key];
      const customTokenWithoutPortalData = customSection?.ContentToken?.replace('portal_data_', '')
      if (customSection?.Enabled !== false && customSection?.ContentToken && customTokenWithoutPortalData && order.CustomSectionTokens?.[customTokenWithoutPortalData]) {
        sectionsConfig.push({
          type: key,
          icon: <SectionIcon icon={ customSection?.Icon ? <Icon className="material-icons-outlined">{customSection.Icon.value}</Icon> : <Icon className="material-icons-outlined">question_mark</Icon>} />,
          name: customSection?.Title || key,
          isStandaloneSection: true,
          secondaryText: '',
          routeTo: customSection?.DisplayType === 'individualPage' ? `${key}` : null,
          searchParams: {},
          sortOrder: customSection?.SortOrder,
          contentToken: customTokenWithoutPortalData,
          contentTokenValue: order.CustomSectionTokens?.[customTokenWithoutPortalData],
          displayType: customSection?.DisplayType
        });
      }
    }
  });
  
  if (sectionsConfig.every(section => section.sortOrder !== undefined && section.sortOrder !== null)) {
    sectionsConfig.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }

  return { sectionsConfig, closingTrackerSectionConfig, closingDetailsSectionConfig  }
}
